import type { TypedDocumentNode } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import type {
  Mutation,
  MutationAddToWatchListArgs,
  MutationRemoveFromWatchListArgs,
} from '@/types/GraphqlSchema';

type AddToWatchListMutationData = Pick<Mutation, 'addToWatchList'>;
type RemoveFromWatchListMutationData = Pick<Mutation, 'removeFromWatchList'>;

const ADD_TO_WATCHLIST_MUTATION: TypedDocumentNode<
  AddToWatchListMutationData,
  MutationAddToWatchListArgs
> = gql`
  mutation AddToWatchList($assetLegacyId: ID!, $assetIsoCode: String!) {
    addToWatchList(assetLegacyId: $assetLegacyId, assetIsoCode: $assetIsoCode) {
      watchList {
        assetLegacyId
        assetIsoCode
      }
    }
  }
`;

const REMOVE_FROM_WATCHLIST_MUTATION: TypedDocumentNode<
  RemoveFromWatchListMutationData,
  MutationRemoveFromWatchListArgs
> = gql`
  mutation RemoveFromWatchList($assetLegacyId: ID!, $assetIsoCode: String!) {
    removeFromWatchList(assetLegacyId: $assetLegacyId, assetIsoCode: $assetIsoCode) {
      watchList {
        assetLegacyId
        assetIsoCode
      }
    }
  }
`;

const useMutateWatchList = () => {
  const [addToWatchListMutation] = useMutation(ADD_TO_WATCHLIST_MUTATION);

  const [removeFromWatchListMutation] = useMutation(REMOVE_FROM_WATCHLIST_MUTATION);

  const addToWatchList = useCallback(
    async (assetLegacyId: string, assetIsoCode: string): Promise<void> => {
      await addToWatchListMutation({
        variables: { assetLegacyId, assetIsoCode },
      });
    },
    [addToWatchListMutation],
  );

  const removeFromWatchList = useCallback(
    async (assetLegacyId: string, assetIsoCode: string): Promise<void> => {
      await removeFromWatchListMutation({
        variables: { assetLegacyId, assetIsoCode },
      });
    },
    [removeFromWatchListMutation],
  );

  return {
    addToWatchList,
    removeFromWatchList,
  };
};

export { useMutateWatchList };
